import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';

import { trackEvent } from '@zep/logger/tracking';
import { zusToast } from '@zep/toast';

import { useMyFoldersWithItems } from '../api';
import { showSaveToFolderModal } from '../showSaveToFolderModal';
import type {
  SaveQuizTarget,
  SaveToFolderOptions,
} from '../types/saveQuizLocation.types';
import {
  reportFolderError,
  toFolderSaveContext,
  toTrackingFailReason,
} from '../utils/folderTracking';

/**
 * 방금 받아 온 목록을 "새 것"으로 인정하는 시간. 연속으로 여닫을 때 매번 기다리지 않게 한다.
 * 정확성은 무효화가 지킨다.
 */
const FOLDER_LIST_FRESH_MS = 60_000;

/**
 * 저장 위치 선택 모달을 목록을 받아 둔 뒤 연다.
 *
 * 모달 안에서 조회를 시작하면 폴더가 도착하면서 높이가 점프하고, "폴더가 없는 사용자"와
 * "아직 안 받은 상태"가 구분되지 않는다.
 *
 * 실패하면 모달을 열지 않는다.
 */
export const useShowSaveToFolderModal = () => {
  const { t } = useTranslation(['common']);
  const queryClient = useQueryClient();
  /** 느린 네트워크에서 연타하면 요청과 모달이 겹친다. 한 번에 하나만 연다. */
  const isOpeningRef = useRef(false);

  return useCallback(
    async (quiz: SaveQuizTarget, options?: SaveToFolderOptions) => {
      if (isOpeningRef.current) return;
      isOpeningRef.current = true;

      const context = toFolderSaveContext(quiz);
      // 여기까지 왔으면 로그인한 사용자다. 비로그인은 호출부가 로그인으로 보내며 따로 찍는다.
      trackEvent('quiz_folder_save_entry_click', {
        ...context,
        auth_required: false,
      });

      let folderCount = 0;
      try {
        const list = await queryClient.fetchInfiniteQuery({
          ...useMyFoldersWithItems.getFetchOptions(),
          staleTime: FOLDER_LIST_FRESH_MS,
          // 전역 기본 재시도는 간격이 30초까지 늘어난다.
          // 그동안 버튼이 죽은 것처럼 보이므로 첫 실패에 바로 알린다.
          retry: false,
        });
        folderCount = list.pages.flatMap(page => page.items).length;
      } catch (error) {
        trackEvent('quiz_folder_save_modal_fail', {
          ...context,
          reason: toTrackingFailReason(error),
        });
        reportFolderError(error, {
          action: 'save_modal_open',
          quizSetId: quiz.id,
        });
        zusToast.error(t('common:folderSave.modal.loadError'), {
          position: 'top-center',
        });
        return;
      } finally {
        isOpeningRef.current = false;
      }

      trackEvent('quiz_folder_save_modal_open', {
        ...context,
        from_folder_id: quiz.currentFolderId,
        folder_count: folderCount,
        private_only: !!options?.privateOnly,
      });
      await showSaveToFolderModal(quiz, options);
    },
    [queryClient, t],
  );
};
